/**
 * Staff appointment-caps panel (DNKADM-0053 U2) — the per-period (day / week / month)
 * cap editor for `GET /v2/admin/staff/{id}/appointment-caps`,
 * `PUT …/appointment-caps/{period}`, `DELETE …/appointment-caps/{period}`
 * (handoff §5.6).
 *
 * One row per period (calendar; week resets Sunday). Each row sets a max-appointments
 * ceiling and, behind an **advanced** disclosure, the `countedStatuses` that count
 * toward it — defaulting to **all six** reservation statuses (§4.8). Counting
 * `cancelled`/`invalid` toward a hard throughput ceiling is intended, so the default
 * is all-on and the per-status control is advanced. Setting a max upserts the cap (at
 * most one per `(staff, period)`); clearing it removes the cap (no cap = uncapped),
 * each with a refetch + toast.
 */
import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import {
  CAP_PERIODS,
  deleteStaffCap,
  RESERVATION_STATUSES,
  upsertStaffCap,
  type CapPeriod,
  type ReservationStatus,
  type StaffAppointmentCap,
} from '@/api/staff';
import { serverMessage } from '@/api/serverMessage';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

/** Human label for a cap period. */
const PERIOD_LABELS: Record<CapPeriod, string> = {
  day: 'Per day',
  week: 'Per week (resets Sunday)',
  month: 'Per calendar month',
};

/** Human label for a reservation status (the §4.8 wire value, made readable). */
const STATUS_LABELS: Record<ReservationStatus, string> = {
  new: 'New (unconfirmed)',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  showed: 'Showed',
  no_show: 'No-show',
  invalid: 'Invalid',
};

interface StaffCapsPanelProps {
  /** The loaded appointment caps for the staff member (≤1 per period). */
  caps: StaffAppointmentCap[];
  /** The staff id, forwarded on writes. */
  staffId: string;
  /** Re-fetch after a successful write (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
}

export function StaffCapsPanel({ caps, staffId, refetch, showToast }: StaffCapsPanelProps) {
  const capByPeriod = useMemo(() => {
    const map = new Map<CapPeriod, StaffAppointmentCap>();
    for (const cap of caps) map.set(cap.period, cap);
    return map;
  }, [caps]);

  return (
    <Stack spacing={2} sx={{ maxWidth: 640 }}>
      {CAP_PERIODS.map((period) => (
        <CapRow
          // Reseed each row when its loaded cap identity changes.
          key={`${period}|${capByPeriod.get(period)?.maxAppointments ?? 'none'}|${
            capByPeriod.get(period)?.countedStatuses.join(',') ?? ''
          }`}
          period={period}
          cap={capByPeriod.get(period) ?? null}
          staffId={staffId}
          refetch={refetch}
          showToast={showToast}
        />
      ))}
    </Stack>
  );
}

/** One period's row: a max input + an advanced counted-statuses disclosure. */
function CapRow({
  period,
  cap,
  staffId,
  refetch,
  showToast,
}: {
  period: CapPeriod;
  cap: StaffAppointmentCap | null;
  staffId: string;
  refetch: () => void;
  showToast: ShowToast;
}) {
  const [max, setMax] = useState(cap ? String(cap.maxAppointments) : '');
  const [counted, setCounted] = useState<ReservationStatus[]>(
    cap ? cap.countedStatuses : [...RESERVATION_STATUSES],
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function toggleStatus(status: ReservationStatus, checked: boolean) {
    setCounted((prev) =>
      checked ? [...prev, status] : prev.filter((s) => s !== status),
    );
  }

  async function handleSave() {
    setError(null);
    const trimmed = max.trim();
    // An empty max removes the cap (uncapped) if one exists; otherwise it's a no-op.
    if (trimmed === '') {
      if (cap === null) return;
      setSaving(true);
      try {
        await deleteStaffCap(staffId, period);
        refetch();
        showToast('Cap removed', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t remove the cap.', 'error');
      } finally {
        setSaving(false);
      }
      return;
    }

    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setError('Enter a whole number (0 or more), or clear it to remove the cap.');
      return;
    }
    if (counted.length === 0) {
      setError('Select at least one status to count toward the cap.');
      return;
    }

    setSaving(true);
    try {
      await upsertStaffCap(staffId, period, { maxAppointments: parsed, countedStatuses: counted });
      refetch();
      showToast('Cap saved', 'success');
    } catch (err) {
      const view = classifyFormError(err, 'The cap couldn’t be saved. Please try again.');
      if (view.kind === 'inline') setError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const allCounted = counted.length === RESERVATION_STATUSES.length;

  return (
    <Paper elevation={1} sx={{ p: 2.25 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, minWidth: 200 }}>
          {PERIOD_LABELS[period]}
        </Typography>
        <TextField
          type="number"
          label="Max appointments"
          size="small"
          value={max}
          onChange={(e) => setMax(e.target.value)}
          disabled={saving}
          placeholder="Uncapped"
          sx={{ width: 180 }}
          slotProps={{
            htmlInput: { min: 0, step: 1, 'aria-label': `${PERIOD_LABELS[period]} max appointments` },
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : cap !== null && max.trim() === '' ? 'Remove cap' : 'Save cap'}
        </Button>
      </Stack>

      <Accordion elevation={0} disableGutters sx={{ mt: 1, '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
        <AccordionSummary
          expandIcon={<Icon name="expand_more" />}
          aria-label={`${PERIOD_LABELS[period]} advanced counted statuses`}
          sx={{ px: 0, minHeight: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Advanced: counted statuses {allCounted ? '(all six)' : `(${counted.length} of 6)`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Which reservation statuses count toward this ceiling. Counting cancelled / invalid
            enforces a hard throughput limit.
          </Typography>
          <FormGroup row>
            {RESERVATION_STATUSES.map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    size="small"
                    checked={counted.includes(status)}
                    onChange={(e) => toggleStatus(status, e.target.checked)}
                    disabled={saving}
                    slotProps={{ input: { 'aria-label': `Count ${STATUS_LABELS[status]}` } }}
                  />
                }
                label={<Typography variant="body2">{STATUS_LABELS[status]}</Typography>}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {error && (
        <Typography role="alert" sx={{ mt: 0.5, fontSize: 12, color: 'error.main' }}>
          {error}
        </Typography>
      )}
    </Paper>
  );
}
